import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Image,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhoto } from '@tabler/icons-react';
import { useMemo } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { HeaderAlignment } from 'src/secret-menu/export-assets/types';

const MAX_LOGO_SIZE = 5 * 1024 * 1024;

const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

type SlotDefinition = {
  slot: string;
  label: string;
  description: string;
};

type SlotCardProps = {
  definition: SlotDefinition;
  file: File | null;
  useCompanyLogo: boolean;
  companyLogoAvailable: boolean;
  alignment: HeaderAlignment;
  onFileChange: (file: File | null) => void;
  onUseCompanyLogoChange: (value: boolean) => void;
  onAlignmentChange: (value: HeaderAlignment) => void;
};

export default function SlotCard({
  definition,
  file,
  useCompanyLogo,
  companyLogoAvailable,
  alignment,
  onFileChange,
  onUseCompanyLogoChange,
  onAlignmentChange,
}: SlotCardProps) {
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const dropzoneDisabled = useCompanyLogo;

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Stack gap={2}>
          <Text fw={FONT_WEIGHTS.semibold}>{definition.label}</Text>
          <Text size="textSm" c="gray.6">
            {definition.description}
          </Text>
        </Stack>

        <Checkbox
          label="Use company logo"
          description={
            companyLogoAvailable
              ? 'Reuse the company logo uploaded above for this slot.'
              : 'Upload a company logo above to enable this option.'
          }
          checked={useCompanyLogo}
          onChange={e => onUseCompanyLogoChange(e.currentTarget.checked)}
          disabled={!companyLogoAvailable}
        />

        {!useCompanyLogo &&
          (file && previewUrl ? (
            <Flex align="center" gap="md" p="sm" bg="gray.0" style={{ borderRadius: 8 }}>
              <Box
                w={64}
                h={64}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Image src={previewUrl} alt={definition.label} fit="contain" h={64} />
              </Box>
              <Stack gap={2} flex={1}>
                <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                  {file.name}
                </Text>
                <Text size="textSm" c="gray.6">
                  {(file.size / 1024).toFixed(0)} KB
                </Text>
              </Stack>
              <Button variant="subtle" color="red" size="xs" onClick={() => onFileChange(null)}>
                Remove
              </Button>
            </Flex>
          ) : (
            <Dropzone
              onDrop={files => {
                const next = files[0];
                if (next) onFileChange(next);
              }}
              onReject={() =>
                notifications.show({
                  title: 'Rejected',
                  message: 'Logo must be an image under 5MB.',
                  color: 'red',
                })
              }
              maxSize={MAX_LOGO_SIZE}
              maxFiles={1}
              accept={IMAGE_MIME_TYPE}
              disabled={dropzoneDisabled}
              style={{
                border: '1px dashed var(--mantine-color-gray-4)',
                borderRadius: 8,
                padding: 24,
              }}
            >
              <Flex direction="column" align="center" gap="xs">
                <Dropzone.Idle>
                  <ThemeIcon variant="light" color="gray" size={40} radius="xl">
                    <IconPhoto size={20} />
                  </ThemeIcon>
                </Dropzone.Idle>
                <Text size="textSm" c="gray.7">
                  Drop a logo for this slot
                </Text>
                <Text size="textXs" c="gray.5">
                  PNG, JPG or SVG - max 5MB
                </Text>
              </Flex>
            </Dropzone>
          ))}

        <Flex align="center" gap="md">
          <Text size="textSm">Alignment</Text>
          <SegmentedControl
            data={ALIGNMENT_OPTIONS}
            value={alignment}
            onChange={value => onAlignmentChange(value as HeaderAlignment)}
          />
        </Flex>
      </Stack>
    </Card>
  );
}
